import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Tooltip,
  InputBase,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
  Chip,
  alpha,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  ConfirmationNumber as TicketsIcon,
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  AccountCircle as ProfileIcon,
  Add as AddIcon,
  MenuBook as KBIcon,
  BarChart as ReportsIcon,
  Settings as SettingsIcon,
  ChevronLeft as CollapseIcon,
  ChevronRight as ExpandIcon,
  Queue as QueueIcon,
  People as CustomersIcon,
  AdminPanelSettings as AdminIcon,
  PersonOutline as UserRoleIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../../stores/authStore';
import { useBranding } from '../../utils/BrandingContext';
import { isHelpdeskMode } from '../../utils/helpdeskMode';
import api, { extractData } from '../../utils/api';

const SIDEBAR_WIDTH = 240;
const SIDEBAR_COLLAPSED = 64;
const HEADER_HEIGHT = 56;

interface NavCounts {
  open: number;
  queue: number; // unassigned
}

const HelpdeskLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { branding } = useBranding();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const basePath = isHelpdeskMode() ? '/support' : '/helpdesk';
  const [collapsed, setCollapsed] = useState(false);
  const [userMenu, setUserMenu] = useState<null | HTMLElement>(null);
  const [counts, setCounts] = useState<NavCounts>({ open: 0, queue: 0 });
  const [showAdminHint, setShowAdminHint] = useState(!isAdmin);
  const sidebarW = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH;

  // Load live counts for sidebar badges
  const loadCounts = useCallback(async () => {
    try {
      const apiPath = isAdmin ? '/support/tickets' : '/support/my-tickets';
      const [openRes, queueRes] = await Promise.all([
        api.get(apiPath, { params: { status: 'open', limit: 1, page: 1 } }),
        isAdmin ? api.get('/support/tickets', { params: { assignedTo: 'none', limit: 1, page: 1 } }) : Promise.resolve(null),
      ]);
      const openData = extractData<{ total?: number }>(openRes);
      setCounts({
        open: openData?.total || 0,
        queue: isAdmin && queueRes ? (extractData<{ total?: number }>(queueRes)?.total || 0) : 0,
      });
    } catch {
      // Silent — counts are decorative
    }
  }, [isAdmin]);

  useEffect(() => {
    loadCounts();
    // Refresh every 2 minutes
    const interval = setInterval(loadCounts, 120000);
    return () => clearInterval(interval);
  }, [loadCounts]);

  // Set browser tab title to "Company Name - Helpdesk"
  useEffect(() => {
    const company = branding.companyName || 'CruvzMail';
    document.title = `${company} - Helpdesk`;
    return () => {
      document.title = `${company} - Webmail`;
    };
  }, [branding.companyName]);

  const navItems = [
    {
      id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon />,
      path: `${basePath}/dashboard`, badge: 0,
    },
    {
      id: 'tickets', label: 'Tickets', icon: <TicketsIcon />,
      path: `${basePath}/tickets`, badge: counts.open,
    },
    ...(isAdmin ? [{
      id: 'queue', label: 'Agent Queue', icon: <QueueIcon />,
      path: `${basePath}/queue`, badge: counts.queue,
    }] : []),
    ...(isAdmin ? [{
      id: 'customers', label: 'Customers', icon: <CustomersIcon />,
      path: `${basePath}/customers`, badge: 0,
    }] : []),
    {
      id: 'knowledge', label: 'Knowledge Base', icon: <KBIcon />,
      path: `${basePath}/knowledge`, badge: 0,
    },
    ...(isAdmin ? [{
      id: 'reports', label: 'Reports', icon: <ReportsIcon />,
      path: `${basePath}/reports`, badge: 0,
    }] : []),
    ...(isAdmin ? [{
      id: 'settings', label: 'Settings', icon: <SettingsIcon />,
      path: `${basePath}/settings`, badge: 0,
    }] : []),
  ];

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  const handleLogout = () => {
    setUserMenu(null);
    logout();
    navigate('/login');
  };

  const getInitials = (name?: string) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <Box sx={{ display: 'flex', height: '100%', bgcolor: '#f6f8fc', overflow: 'hidden' }}>
      {/* Sidebar — white hybrid style matching webmail */}
      <Box
        sx={{
          width: sidebarW,
          minWidth: sidebarW,
          height: '100%',
          bgcolor: '#fff',
          borderRight: '1px solid #e0e4e8',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.3s ease, min-width 0.3s ease',
          overflow: 'hidden',
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            px: collapsed ? 0 : 2,
            py: 1.5,
            minHeight: 56,
            cursor: 'pointer',
            flexShrink: 0,
          }}
          onClick={() => navigate(`${basePath}/dashboard`)}
        >
          <Box
            component="img"
            src={branding.logo || '/logo.png'}
            alt={branding.companyName || 'Helpdesk'}
            sx={{
              height: 32,
              maxWidth: collapsed ? 32 : 120,
              objectFit: 'contain',
              objectPosition: 'left center',
              ml: collapsed ? 'auto' : 0,
              mr: collapsed ? 'auto' : 0,
            }}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.src = '/logo-icon.png';
            }}
          />
        </Box>

        {/* New Ticket button — styled like webmail Compose */}
        <Box sx={{ px: collapsed ? 0 : 1.5, pb: 1, display: 'flex', justifyContent: collapsed ? 'center' : 'flex-start', flexShrink: 0 }}>
          <Box
            component="button"
            onClick={() => navigate(`${basePath}/tickets?new=true`)}
            sx={{
              width: collapsed ? '46px' : '148px',
              height: '52px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: '10px',
              px: collapsed ? 0 : '18px',
              background: '#1967d2',
              color: '#fff',
              border: 'none',
              borderRadius: collapsed ? '50%' : '26px',
              fontSize: '0.875rem',
              fontWeight: 500,
              fontFamily: "'Google Sans', 'Inter', 'Roboto', -apple-system, sans-serif",
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2), 0 1px 2px rgba(0,0,0,0.12)',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: '#1a73e8',
                boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            <AddIcon sx={{ fontSize: 22, flexShrink: 0 }} />
            {!collapsed && 'New Ticket'}
          </Box>
        </Box>

        {/* Nav Items */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: collapsed ? 0.5 : 1, py: 0.5, '&::-webkit-scrollbar': { width: 0 } }}>
          {navItems.map((item) => {
            const active = isActive(item.path);
            const hasBadge = item.badge > 0;
            return (
              <Tooltip key={item.id} title={collapsed ? `${item.label}${hasBadge ? ` (${item.badge})` : ''}` : ''} placement="right" arrow>
                <Box
                  onClick={() => navigate(item.path)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: collapsed ? 0 : 1.5,
                    py: 0.875,
                    my: 0.125,
                    borderRadius: collapsed ? '50%' : '0 24px 24px 0',
                    cursor: 'pointer',
                    color: active ? '#1a73e8' : '#444746',
                    bgcolor: active ? '#e8f0fe' : 'transparent',
                    fontWeight: active ? 700 : 400,
                    fontSize: '0.875rem',
                    fontFamily: "'Google Sans', 'Inter', 'Roboto', -apple-system, sans-serif",
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    width: collapsed ? '46px' : 'auto',
                    height: collapsed ? '46px' : 'auto',
                    mx: collapsed ? 'auto' : 0,
                    transition: 'all 0.15s',
                    '&:hover': {
                      bgcolor: active ? '#e8f0fe' : '#f1f3f4',
                    },
                    '& .MuiSvgIcon-root': { fontSize: 20, flexShrink: 0, color: active ? '#1a73e8' : '#444746' },
                  }}
                >
                  <Badge
                    badgeContent={item.badge > 99 ? '99+' : item.badge}
                    invisible={!hasBadge || collapsed}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.6rem',
                        height: 16,
                        minWidth: 16,
                        padding: '0 3px',
                        bgcolor: item.id === 'queue' ? '#f59e0b' : '#d93025',
                        color: '#fff',
                      },
                    }}
                  >
                    {item.icon}
                  </Badge>
                  {!collapsed && (
                    <>
                      <span style={{ flex: 1 }}>{item.label}</span>
                      {hasBadge && (
                        <Box
                          sx={{
                            minWidth: 20,
                            height: 18,
                            borderRadius: '9px',
                            bgcolor: item.id === 'queue' ? '#fef3c7' : '#fce8e6',
                            color: item.id === 'queue' ? '#92400e' : '#c5221f',
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 0.75,
                          }}
                        >
                          {item.badge > 99 ? '99+' : item.badge}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Tooltip>
            );
          })}

          {/* Non-admin hint */}
          {!isAdmin && !collapsed && (
            <Box
              sx={{
                mt: 2,
                mx: 1,
                p: 1.25,
                borderRadius: '12px',
                bgcolor: '#f0f4ff',
                border: '1px solid #c7d7fb',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                <AdminIcon sx={{ fontSize: 14, color: '#1967d2' }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#1967d2' }}>Admin Features</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.68rem', color: '#5f6368', lineHeight: 1.5 }}>
                Queue, Customers, Reports & Settings require admin access.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Bottom: Role + Collapse */}
        <Box sx={{ borderTop: '1px solid #e0e4e8', flexShrink: 0 }}>
          {!collapsed && (
            <Box
              sx={{
                px: 2,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {isAdmin
                ? <AdminIcon sx={{ fontSize: 14, color: '#1967d2' }} />
                : <UserRoleIcon sx={{ fontSize: 14, color: '#9aa0a6' }} />}
              <Typography sx={{ fontSize: '0.7rem', color: isAdmin ? '#1967d2' : '#9aa0a6', fontWeight: 600 }}>
                {user?.role === 'superadmin' ? 'Super Admin' : user?.role || 'User'}
              </Typography>
            </Box>
          )}
          <Box
            onClick={() => setCollapsed(!collapsed)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 0.875,
              cursor: 'pointer',
              color: '#9aa0a6',
              '&:hover': { bgcolor: '#f1f3f4', color: '#444746' },
              transition: 'all 0.15s',
            }}
          >
            {collapsed ? <ExpandIcon fontSize="small" /> : <CollapseIcon fontSize="small" />}
          </Box>
        </Box>
      </Box>

      {/* Main Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Non-admin banner */}
        {!isAdmin && showAdminHint && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2.5,
              py: 0.75,
              bgcolor: '#e8f0fe',
              borderBottom: '1px solid #c7d7fb',
              flexShrink: 0,
            }}
          >
            <InfoIcon sx={{ fontSize: 16, color: '#1967d2' }} />
            <Typography sx={{ fontSize: '0.8rem', color: '#3c4043', flex: 1 }}>
              You're viewing the <b>user</b> view. Log in with your <b>admin account</b> to access all features.
            </Typography>
            <Chip
              label="Sign in as Admin"
              size="small"
              onClick={() => { logout(); navigate('/login/direct'); }}
              sx={{ bgcolor: '#1967d2', color: '#fff', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', '&:hover': { bgcolor: '#1558b0' } }}
            />
            <IconButton size="small" onClick={() => setShowAdminHint(false)} sx={{ color: '#9aa0a6' }}>
              <Box sx={{ fontSize: 16, lineHeight: 1 }}>×</Box>
            </IconButton>
          </Box>
        )}

        {/* Header */}
        <Box
          sx={{
            height: HEADER_HEIGHT,
            minHeight: HEADER_HEIGHT,
            bgcolor: '#fff',
            borderBottom: '1px solid #e0e4e8',
            display: 'flex',
            alignItems: 'center',
            px: 2.5,
            gap: 2,
          }}
        >
          {/* Search */}
          <Box
            sx={{
              flex: 1,
              maxWidth: 520,
              display: 'flex',
              alignItems: 'center',
              bgcolor: '#f1f3f4',
              borderRadius: '24px',
              px: 1.75,
              py: 0.25,
              transition: 'background 0.15s',
              '&:focus-within': { bgcolor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.15)', outline: '1px solid #e0e4e8' },
            }}
          >
            <SearchIcon sx={{ color: '#9aa0a6', fontSize: 20, mr: 1, flexShrink: 0 }} />
            <InputBase
              placeholder="Search tickets..."
              sx={{
                flex: 1,
                fontSize: '0.875rem',
                fontFamily: "'Google Sans', 'Inter', 'Roboto', -apple-system, sans-serif",
                color: '#3c4043',
                '& input': { p: 0, py: 0.625 },
                '& input::placeholder': { color: '#9aa0a6' },
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                  const val = (e.target as HTMLInputElement).value;
                  if (val) navigate(`${basePath}/tickets?search=${encodeURIComponent(val)}`);
                }
              }}
            />
          </Box>

          <Box sx={{ flex: 1 }} />

          {/* Role chip */}
          <Chip
            icon={isAdmin ? <AdminIcon sx={{ fontSize: '14px !important' }} /> : <UserRoleIcon sx={{ fontSize: '14px !important' }} />}
            label={user?.role === 'superadmin' ? 'Super Admin' : user?.role === 'admin' ? 'Admin' : 'User'}
            size="small"
            sx={{
              height: 26,
              fontSize: '0.7rem',
              fontWeight: 600,
              bgcolor: isAdmin ? '#e8f0fe' : '#f1f3f4',
              color: isAdmin ? '#1967d2' : '#5f6368',
              border: 'none',
              '& .MuiChip-icon': { color: 'inherit' },
            }}
          />

          {/* Notifications */}
          <Tooltip title="Notifications">
            <IconButton size="small" sx={{ color: '#5f6368', '&:hover': { bgcolor: '#f1f3f4' } }}>
              <Badge badgeContent={0} color="error" max={9}>
                <NotificationsIcon sx={{ fontSize: 22 }} />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* User Avatar */}
          <Tooltip title={`${user?.firstName || ''} ${user?.lastName || ''} (${user?.email})`.trim()}>
            <IconButton onClick={(e) => setUserMenu(e.currentTarget)} sx={{ p: 0.25 }}>
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: isAdmin ? '#1967d2' : '#9aa0a6',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                }}
              >
                {getInitials(user?.firstName || user?.email)}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={userMenu}
            open={Boolean(userMenu)}
            onClose={() => setUserMenu(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { width: 248, mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' } }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#3c4043' }}>
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#5f6368', mt: 0.25 }}>
                {user?.email}
              </Typography>
            </Box>
            <Divider sx={{ borderColor: '#e0e4e8' }} />
            <MenuItem onClick={() => { setUserMenu(null); navigate('/profile'); }} sx={{ fontSize: '0.875rem', py: 1 }}>
              <ListItemIcon><ProfileIcon fontSize="small" sx={{ color: '#5f6368' }} /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>Profile</ListItemText>
            </MenuItem>
            <Divider sx={{ borderColor: '#e0e4e8' }} />
            <MenuItem onClick={handleLogout} sx={{ fontSize: '0.875rem', py: 1 }}>
              <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', color: '#d93025' }}>Sign Out</ListItemText>
            </MenuItem>
          </Menu>
        </Box>

        {/* Content Area */}
        <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#f6f8fc' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default HelpdeskLayout;
